import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import MediaQuery from 'react-responsive';
import styles from './RegimenResult.module.sass';
import BackButton from '../../../quiz/components/back_button/BackButton';
import Settings from './settings/Settings';
import Button from '../../../ui_kit/button/Button';
import ClickableDiv from '../../../ui_kit/clickable_div/ClickableDiv';
import Products from './products/Products';
import COMPLEXITY from '../../constants/complexity';
import DAY_OR_NIGHT from '../../constants/day_or_night';
import { productsActions } from '../../../product/actions';
import { productsObjectsSelector, productsLoadingSelector } from '../../../products/selectors/products';
import BREAKPOINTS from '../../constants/breakpoints';
import SKIN_TYPES from '../../constants/skin_types';
import { COUPON_CODE } from './checkout_code/CheckoutCode';
import CATEGORIES from '../../constants/categories';
import defaultSelectedCategories from './default_categories';
import api from '../../api';
import { modalsActions } from '../../../modals/actions';
import MODALS from '../../../modals/constants/modals';
import { userObjectSelector } from '../../../user/selectors/user';

const EXISTING_PRODUCT_SCORE = 100;

function Buttons({
  bottom, emailLoading, productsLoading, onAddAllToBag, onSendEmail,
}) {
  return (
    <div className={`${styles['buttons-list']} ${bottom ? styles.bottom : null}`}>
      <MediaQuery query={`(max-width: ${BREAKPOINTS.DESKTOP_WIDTH_BREAKPOINT - 1}px)`}>
        <Button
          className={styles['add-to-bag']}
          size="small"
          text="ADD ALL TO BAG"
          color="green"
          loading={productsLoading}
          onClickHandler={onAddAllToBag}
        />
        <div className={styles.buttons}>
          <Button
            className={styles.button}
            size="small"
            text="START OVER"
            color="transparent-green"
            href="/regimen_finder/"
          />
          <Button
            className={styles.button}
            size="small"
            color="transparent-green"
            loading={emailLoading}
            onClickHandler={onSendEmail}
          >
            <span className={styles['button-text']}>SEND ME MY REGIMEN</span>
          </Button>
        </div>
      </MediaQuery>
      <MediaQuery query={`(min-width: ${BREAKPOINTS.DESKTOP_WIDTH_BREAKPOINT}px)`}>
        <div className={styles['buttons-wrapper']}>
          <Button
            className={styles.button}
            size="small"
            color="green"
            loading={emailLoading}
            onClickHandler={onSendEmail}
          >
            <span className={styles['button-text']}>SEND ME MY REGIMEN</span>
          </Button>
          <div>
            <ClickableDiv
              className={styles['start-over']}
              onClick={() => { window.location.href = '/regimen_finder/'; }}
            >
              Start over
            </ClickableDiv>
            <Button
              className={styles['add-to-bag']}
              size="small"
              text="ADD ALL TO BAG"
              color="transparent-green"
              loading={productsLoading}
              onClickHandler={onAddAllToBag}
            />
          </div>
        </div>
      </MediaQuery>
    </div>
  );
}

Buttons.propTypes = {
  bottom: PropTypes.bool,
  emailLoading: PropTypes.bool.isRequired,
  productsLoading: PropTypes.bool.isRequired,
  onAddAllToBag: PropTypes.func.isRequired,
  onSendEmail: PropTypes.func.isRequired,
};

const generatePageTitle = (skinType, concerns) => {
  let title = `The perfect routine for ${skinType.toLowerCase()} skin that addresses `;
  if (concerns.length === 1) {
    title += concerns[0].toLowerCase();
  } else if (concerns.length === 2) {
    title += `${concerns[0].toLowerCase()} and ${concerns[1].toLowerCase()}`;
  } else if (concerns.length === 3) {
    title += `${concerns[0].toLowerCase()}, ${concerns[1].toLowerCase()} and ${concerns[2].toLowerCase()}`;
  }

  return title;
};

function RegimenResult(props) {
  const {
    step, decreaseStep, regimen, allProducts, addProducts,
    products: productsFromProps, addToBag, addToBagMultiple, email,
    productsLoading, productsTaggings, openRegimenSentModal, user, openSignUpModal,
  } = props;

  const {
    skinType, isSensitiveSkin, concerns, selectedProductIds,
  } = regimen;

  useEffect(() => {
    api.saveRegimen(
      {
        regimen_finder_result: {
          skin_type: skinType,
          sensitive_skin: isSensitiveSkin,
          concerns: concerns.join(', '),
          selected_product_ids: selectedProductIds.join(', '),
          email: email || (user && user.email),
        },
      },
    );
  }, []);

  const [complexity, setComplexity] = useState(COMPLEXITY.COMPLEX);
  const [dayOrNight, setDayOrNight] = useState(DAY_OR_NIGHT.DAY);
  const [productIds, setProductIds] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(
    defaultSelectedCategories(COMPLEXITY.COMPLEX, DAY_OR_NIGHT.DAY),
  );
  const [availableCategories, setAvailableCategories] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);

  const title = generatePageTitle(skinType, concerns);

  const productScore = (productId) => {
    let result = 0;

    const calculateTagging = (tagging) => {
      if (tagging) {
        result += tagging.weight.value;
      }
    };

    const productTaggings = productsTaggings[productId];

    const skinTypeTagging = productTaggings.find(
      (tagging) => tagging.tag.name === skinType.toLowerCase(),
    );
    calculateTagging(skinTypeTagging);

    const sensitivityTagging = productTaggings.find(
      (tagging) => tagging.tag.name === (
        isSensitiveSkin ? SKIN_TYPES.SENSITIVE : SKIN_TYPES.NOT_SENSITIVE
      ).toLowerCase(),
    );

    calculateTagging(sensitivityTagging);

    const concernTaggings = productTaggings.filter(
      (tagging) => concerns.map((concern) => concern.toLowerCase()).includes(tagging.tag.name),
    );

    concernTaggings.forEach((tagging) => calculateTagging(tagging));

    if (selectedProductIds.includes(productId)) {
      result += EXISTING_PRODUCT_SCORE;
    }

    return result;
  };

  useEffect(() => {
    addProducts(allProducts);
  }, [allProducts.length]);

  useEffect(() => {
    let filteredProducts = productsFromProps.filter(
      (product) => !product.waitlistSubscribable
        && (
          !product.applicationTime
          || product.applicationTime === dayOrNight
          || product.applicationTime === DAY_OR_NIGHT.DAY_AND_NIGHT
        ),
    );

    const productsCategories = [
      ...new Set(filteredProducts.map((product) => product.regimenCategory)),
    ];

    const availableProductsCategories = Object.values(CATEGORIES).filter(
      (category) => productsCategories.includes(category)
        && (dayOrNight === DAY_OR_NIGHT.DAY || category !== CATEGORIES.BRONZER),
    );

    setAvailableCategories(
      availableProductsCategories,
    );

    if (selectedCategories.length > 0) {
      const newFilteredProducts = filteredProducts.filter(
        (product) => selectedCategories.includes(product.regimenCategory),
      );
      // fix case when selected category present in one tab, but doesn't exist in other tab
      if (newFilteredProducts.length > 0) {
        filteredProducts = newFilteredProducts;
      }
    }

    filteredProducts.forEach((product) => {
      // eslint-disable-next-line no-console
      console.log(`${product.name} - ${productScore(product.id)}`);
    });

    const sortedProductsByScore = filteredProducts.sort(
      (productA, productB) => (productScore(productA.id) < productScore(productB.id) ? 1 : -1),
    );

    const productByCategory = {};
    availableProductsCategories.forEach((category) => {
      productByCategory[category] = sortedProductsByScore.find(
        (product) => product.regimenCategory === category,
      );
    });

    let resultProducts = selectedCategories.map(
      (selectedCategory) => productByCategory[selectedCategory],
    ).filter((product) => product);
    if (resultProducts.length === 0) {
      resultProducts = availableProductsCategories.map(
        (category) => category !== CATEGORIES.BRONZER && productByCategory[category],
      ).filter((product) => product);
    }

    setProductIds(
      resultProducts.map((product) => product.id),
    );
  }, [productsFromProps.length, complexity, dayOrNight, selectedCategories.length]);

  const onAddAllToBag = () => {
    const productsToAdd = productsFromProps.filter((product) => productIds.includes(product.id));
    const variantIds = productsToAdd.map((productToAdd) => {
      if (productToAdd.variants.length === 0) {
        return productToAdd.variantId;
      }

      return productToAdd.variants.sort(
        (variantA, variantB) => (Number(variantA.price) < Number(variantB.price) ? 1 : -1),
      )[0].id;
    });
    addToBagMultiple(productIds, variantIds, COUPON_CODE);
  };

  useEffect(() => {
    if (window.userLoggedInCallback) {
      window.userLoggedInCallback(user);
      window.userLoggedInCallback = null;
    }
  }, [!!user]);

  const sendEmail = (emailAddress) => {
    setEmailLoading(true);
    const productsToAdd = productIds.map(
      (productId) => productsFromProps.find((product) => product.id === productId),
    );
    const variantIds = productsToAdd.map((productToAdd) => {
      if (productToAdd.variants.length === 0) {
        return productToAdd.variantId;
      }

      return productToAdd.variants.sort(
        (variantA, variantB) => (Number(variantA.price) < Number(variantB.price) ? 1 : -1),
      )[0].id;
    });
    api.sendEmail(variantIds, emailAddress).then(() => {
      openRegimenSentModal();
      setEmailLoading(false);
    });
  };

  const onSendEmail = () => {
    if (emailLoading) { return; }

    if (!user) {
      openSignUpModal();

      window.userLoggedInCallback = (loggedInUser) => {
        sendEmail(loggedInUser.email);
      };

      return;
    }

    sendEmail(user.email);
  };

  return (
    <div className={styles['regimen-result']}>
      <div className={styles.content}>
        <BackButton decreaseStep={() => { decreaseStep({ ...regimen }); }} />
        {step}
        <h1 className={styles.title}>
          {title}
        </h1>
      </div>
      <MediaQuery query={`(max-width: ${BREAKPOINTS.DESKTOP_WIDTH_BREAKPOINT - 1}px)`}>
        <Settings
          complexity={complexity}
          setComplexity={setComplexity}
          dayOrNight={dayOrNight}
          setDayOrNight={setDayOrNight}
          availableCategories={availableCategories}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          productsCount={productIds.length}
        />
        {/* <CheckoutCode /> */}
        <Buttons
          emailLoading={emailLoading}
          productsLoading={productsLoading}
          onAddAllToBag={onAddAllToBag}
          onSendEmail={onSendEmail}
        />
        <Products
          products={productIds.map(
            (productId) => productsFromProps.find((product) => productId === product.id),
          )}
          addToBag={addToBag}
        />
        {/* <CheckoutCode /> */}
        <Buttons
          bottom
          emailLoading={emailLoading}
          productsLoading={productsLoading}
          onAddAllToBag={onAddAllToBag}
          onSendEmail={onSendEmail}
        />
      </MediaQuery>
      <MediaQuery query={`(min-width: ${BREAKPOINTS.DESKTOP_WIDTH_BREAKPOINT}px)`}>
        {/* <CheckoutCode /> */}
        <Buttons
          emailLoading={emailLoading}
          productsLoading={productsLoading}
          onAddAllToBag={onAddAllToBag}
          onSendEmail={onSendEmail}
        />
        <div className={styles.row}>
          <Settings
            complexity={complexity}
            setComplexity={setComplexity}
            dayOrNight={dayOrNight}
            setDayOrNight={setDayOrNight}
            availableCategories={availableCategories}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
          />
          <Products
            products={productIds.map(
              (productId) => productsFromProps.find((product) => productId === product.id),
            )}
            addToBag={addToBag}
          />
        </div>
        <Buttons
          bottom
          emailLoading={emailLoading}
          productsLoading={productsLoading}
          onAddAllToBag={onAddAllToBag}
          onSendEmail={onSendEmail}
        />
        {/* <CheckoutCode /> */}
      </MediaQuery>
    </div>
  );
}

RegimenResult.propTypes = {
  step: PropTypes.node.isRequired,
  decreaseStep: PropTypes.func.isRequired,
  regimen: PropTypes.shape({
    skinType: PropTypes.string,
    concerns: PropTypes.arrayOf(PropTypes.string),
    selectedProductIds: PropTypes.arrayOf(PropTypes.number),
    isSensitiveSkin: PropTypes.bool,
  }).isRequired,
  allProducts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  addProducts: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  addToBag: PropTypes.func.isRequired,
  addToBagMultiple: PropTypes.func.isRequired,
  productsLoading: PropTypes.bool.isRequired,
  productsTaggings: PropTypes.shape({}).isRequired,
  openSignUpModal: PropTypes.func.isRequired,
  openRegimenSentModal: PropTypes.func.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string,
  }),
  email: PropTypes.string,
};

const mapStateToProps = (state) => ({
  products: productsObjectsSelector(state),
  productsLoading: productsLoadingSelector(state),
  user: userObjectSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  addProducts: (products) => dispatch(productsActions.addProducts(products)),
  addToBag: (id) => dispatch(productsActions.addToBagRequest(id)),
  addToBagMultiple: (ids, variantIds, couponCode) => dispatch(
    productsActions.addToBagMultipleRequest({ ids, variantIds, couponCode }),
  ),
  openSignUpModal: () => dispatch(modalsActions.openModalRequest({
    modal: MODALS.SIGN_UP,
  })),
  openRegimenSentModal: () => dispatch(modalsActions.openModalRequest({
    modal: MODALS.REGIMEN_SENT,
  })),
});

export default connect(mapStateToProps, mapDispatchToProps)(RegimenResult);
